import { FilterValue } from '../types/filter-value.type';
import { Measure } from './measure.enum';
import { Resolution } from './resolution.enum';

/**
 * @deprecated will be replaced with InsightType
 */
export enum WidgetType {
  FEATURE_IMPORTANCE = 'FEATURE_IMPORTANCE',
  FORECAST_VERSIONS = 'FORECAST_VERSIONS',
  PORTFOLIO_TOTALS = 'PORTFOLIO_TOTALS',
  SINGLE_FORECASTS = 'SINGLE_FORECASTS',
  YEARLY_COMPARISON = 'YEARLY_COMPARISON',
  PORTFOLIO_TOTALS_MULTI_YEAR = 'PORTFOLIO_TOTALS_MULTI_YEAR',
  PLANNING_ACCURACY = 'PLANNING_ACCURACY',
  ABC_XYZ_ANALYSIS = 'ABC_XYZ_ANALYSIS',

  // following widgets are not decoupled from forecast-series/forecast chart yet
  PLACEHOLDER = 'PLACEHOLDER',
  FORECAST = 'FORECAST',
  META_INFO = 'META_INFO',
  PORTFOLIO_DEVELOPMENT = 'PORTFOLIO_DEVELOPMENT',
  INSIGHTS = 'INSIGHTS',
  YEAR_TO_DATE_TREND = 'YEAR_TO_DATE_TREND',
  YEAR_TO_YEAR = 'YEAR_TO_YEAR',
}

export type Widget =
  | FeatureImportanceWidget
  | PlaceholderWidget
  | ForecastWidget
  | PortfolioTotalsWidget
  | MetaInfoWidget
  | ForecastVersionsWidget
  | PortfolioDevelopmentWidget
  | InsightsWidget
  | YearToDateTrendWidget
  | SingleForecastsWidget
  | YearToYearWidget
  | PortfolioTotalsMultiYearWidget
  | YearlyComparisonWidget
  | PlanningAccuracyWidget
  | AbcXyzAnalysisWidget;

// marker interface for widgets and shared base props
export interface WidgetBase {
  type: WidgetType;
  name?: string;
}
export interface FeatureImportanceWidget extends WidgetBase {
  type: WidgetType.FEATURE_IMPORTANCE;
}

export interface PlaceholderWidget extends WidgetBase {
  type: WidgetType.PLACEHOLDER;
}

export interface ForecastWidget extends WidgetBase {
  type: WidgetType.FORECAST;
}

export interface PortfolioTotalsWidget extends WidgetBase {
  type: WidgetType.PORTFOLIO_TOTALS;
  /**
   * in case no targetUnit is provided, the default targetUnit from configuration will be used
   */
  targetUnit?: string;

  /**
   * otherwise falls back to first available filter from forecast series response
   */
  filter?: string;

  /**
   * otherwise falls back to the current year
   */
  year?: string;
}

export interface PortfolioTotalsMultiYearWidget extends WidgetBase {
  type: WidgetType.PORTFOLIO_TOTALS_MULTI_YEAR;
  /**
   * in case no targetUnit is provided, the default targetUnit from configuration will be used
   */
  targetUnit?: string;

  /**
   * otherwise falls back to first available filter from forecast series response
   */
  filter?: string;

  /**
   * otherwise falls back to the current year
   */
  years?: number[];
}

export interface MetaInfoWidget extends WidgetBase {
  type: WidgetType.META_INFO;
}

export interface ForecastVersionsWidget extends WidgetBase {
  type: WidgetType.FORECAST_VERSIONS;
}

export interface PortfolioDevelopmentWidget extends WidgetBase {
  type: WidgetType.PORTFOLIO_DEVELOPMENT;
}

export interface InsightsWidget extends WidgetBase {
  type: WidgetType.INSIGHTS;
}

export interface YearToDateTrendWidget extends WidgetBase {
  type: WidgetType.YEAR_TO_DATE_TREND;
  /**
   * if not provided -> "value" is used as fallback
   */
  targetUnit?: string;

  /**
   * otherwise falls back to first available filter from forecast series response
   */
  filterColumn?: string;

  filterOptions?: FilterValue[];
}

export interface SingleForecastsWidget extends WidgetBase {
  type: WidgetType.SINGLE_FORECASTS;
  /**
   * in case no resolution is provided, the default resolution will the lowest possible resolution from configuration
   */
  resolution?: Resolution;
  /**
   * in case no targetUnit is provided, the default targetUnit from configuration will be used
   */
  targetUnit?: string;

  /**
   * otherwise falls back to first available filter from forecast series response
   */
  filterColumn?: string;

  filterOptions?: FilterValue[];
}
export interface YearlyComparisonWidget extends WidgetBase {
  type: WidgetType.YEARLY_COMPARISON;
  /**
   * in case no resolution is provided, the default resolution will the lowest possible resolution from configuration
   */
  resolution?: Resolution;
  /**
   * in case no targetUnit is provided, the default targetUnit from configuration will be used
   */
  targetUnit?: string;

  /**
   * otherwise falls back to first available filter from forecast series response
   */
  filterColumn?: string;

  filterOptions?: FilterValue;
}

export interface YearToYearWidget extends WidgetBase {
  type: WidgetType.YEAR_TO_YEAR;
  /**
   * if not provided -> "value" is used as fallback
   */
  targetUnit?: string;

  /**
   * otherwise falls back to first available filter from forecast series response
   */
  filterColumn?: string;

  filterOptions?: FilterValue[];
}

export interface PlanningAccuracyWidget extends WidgetBase {
  type: WidgetType.PLANNING_ACCURACY;

  resolution?: Resolution;

  planningPeriod?: number;

  selectedFilterGroup?: string;

  selectedFilterOption?: FilterValue;

  additionalTimeSeriesName?: string;
}

export interface AbcXyzAnalysisWidget extends WidgetBase {
  type: WidgetType.ABC_XYZ_ANALYSIS;
  planningPeriod: number;
  errorMeasure: Measure;
  groups: string[];
  volumeBinLimits: number[];
  forecastAccuracyBinLimits: number[];
  timePeriodBackwardsInMonths: number;
  targetUnit?: string;
}
